import type { SupabaseClient } from "@supabase/supabase-js";
import { ParsedRecipe } from "../models/parsed-recipe";
import { RecipeDifficulty } from "../models/recipe";
import { DietaryTag, normalizeDietary } from "../models/dietary";
import { parseIngredientLineWithSubstitute } from "../utils/parseIngredientLine";

// Shape returned by the import-recipe Edge Function.
interface ImportedRecipe {
  title: string;
  image?: string;
  servings?: number;
  cookingDuration?: number;
  difficulty?: string;
  dietary?: string[];
  ingredients: string[];
  steps: string[];
}

// Normalise a user-typed URL: add https:// if they omitted the scheme.
export function normalizeUrl(input: string): string {
  const trimmed = input.trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

const BROWSER_UA =
  "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 " +
  "(KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36";

// Fetch the page from the client — a residential IP isn't blocked the way our
// server's is (e.g. AllRecipes returns 402 to the Edge Function). On web, CORS
// may block this cross-origin fetch; if so it simply returns undefined and the
// blocked-site path reports a clean failure (server-first still covers most sites).
async function fetchPageHtml(url: string): Promise<string | undefined> {
  try {
    const res = await fetch(url, { headers: { "User-Agent": BROWSER_UA } });
    if (res.ok) return await res.text();
  } catch {
    // network/blocked/CORS — nothing more we can do
  }
  return undefined;
}

// Result of an import attempt — a recipe, a human-readable failure reason, and
// (on a soft failure) `pasteText`: the page's text ready for manual cleanup, which
// the caller can hand to Quick Paste pre-filled instead of dead-ending.
export type ImportResult =
  | { ok: true; recipe: ParsedRecipe }
  | { ok: false; reason: string; pasteText?: string };

// Server-first: send just the URL (small request); the function fetches and
// parses. Only if the server is blocked (`retryWithHtml`) do we fetch the page
// client-side and resend the HTML. `supabase` is injected so both apps reuse it.
export async function importRecipeFromUrl(
  supabase: SupabaseClient,
  url: string,
  // The recipe id assigned up-front by the caller, so the function re-hosts the
  // image under `${userId}/${recipeId}.jpg` (matching user-uploaded images). The
  // caller must reuse this same id when it saves the recipe.
  recipeId?: string,
): Promise<ImportResult> {
  let { data, error } = await supabase.functions.invoke("import-recipe", {
    body: { url, recipeId },
  });

  if (!error && data && !data.ok && data.retryWithHtml) {
    const html = await fetchPageHtml(url);
    if (html) {
      ({ data, error } = await supabase.functions.invoke("import-recipe", {
        body: { url, html, recipeId },
      }));
    } else {
      return {
        ok: false,
        reason:
          "This site blocks importing in the browser. Try importing it in the RecipeRoll mobile app, or add the recipe manually.",
      };
    }
  }

  if (error) {
    return {
      ok: false,
      reason: "We couldn't reach the import service. Check your connection and try again.",
    };
  }
  if (!data?.ok) {
    // No structured recipe, but the function returned the page's text for manual
    // cleanup — pass it up as `pasteText` (title as a leading heading line) so the
    // caller can open Quick Paste pre-filled rather than dead-ending.
    if (data?.needsManualCleanup && typeof data.rawText === "string" && data.rawText.trim()) {
      const title = typeof data.title === "string" ? data.title.trim() : "";
      const body = data.rawText.trim();
      return {
        ok: false,
        reason: data.reason ?? "We couldn't read that page.",
        pasteText: title ? `# ${title}\n\n${body}` : body,
      };
    }
    return { ok: false, reason: data?.reason ?? "We couldn't read that page." };
  }
  if (!data.recipe) {
    return { ok: false, reason: "No recipe found on that page." };
  }

  const r = data.recipe as ImportedRecipe;
  const difficulty = Object.values(RecipeDifficulty).find(
    (d) => d.toLowerCase() === String(r.difficulty ?? "").toLowerCase(),
  );

  // Keep only slugs that are real DietaryTags, then normalise (vegan→vegetarian).
  const validTags = Object.values(DietaryTag);
  const dietary = normalizeDietary(
    (r.dietary ?? []).filter((t): t is DietaryTag => validTags.includes(t as DietaryTag)),
  );

  return {
    ok: true,
    recipe: {
      title: r.title ?? "",
      sourceUrl: url,
      image: r.image,
      servings: r.servings,
      cookingDuration: r.cookingDuration,
      difficulty,
      dietary: dietary.length ? dietary : undefined,
      ingredients: (r.ingredients ?? []).map((line) => parseIngredientLineWithSubstitute(line)),
      methodSteps: r.steps ?? [],
      lowConfidence: data.provider === "scrape",
    },
  };
}
